import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingBag, Heart, BadgeCheck, Image as ImageIcon, Quote, X, ChevronLeft, ChevronRight, ShieldCheck, Truck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { shopifyFetch } from '@/shopify';
import { useCartStore, useWishlistStore } from '@/store';
import { toast } from 'sonner';
import { formatDate } from '@/lib/date';
import DOMPurify from 'dompurify';

const ProductPage = () => {
  const params = useParams();
  const id = decodeURIComponent(params.id);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(10);
  const { addItem } = useCartStore();
  const { toggleItem, isInWishlist } = useWishlistStore();

  const [judgeMeProductId, setJudgeMeProductId] = useState('');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [reviewLightboxOpen, setReviewLightboxOpen] = useState(false);
  const [reviewLightboxIndex, setReviewLightboxIndex] = useState(0);
  const addToCartRef = useRef(null);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);

      try {
        const data = await shopifyFetch(
          `
          query ($id: ID!) {
            product(id: $id) {
              id
              title
              handle
              descriptionHtml
              productType
              images(first: 10) {
                edges {
                  node {
                    url
                  }
                }
              }
              variants(first: 20) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                    }
                    compareAtPrice {
                      amount
                    }
                    image {
                      url
                    }
                  }
                }
              }
            }
          }
          `,
          { id }
        );

        const node = data.product;

        if (!node) {
          setProduct(null);
          return;
        }

        const images = node.images.edges.map((e) => e.node.url);
        const variants = node.variants.edges.map((e) => e.node);
        const firstVariant = variants[0];

        setProduct({
          id: node.id,
          numericId: node.id.split('/').pop(),
          name: node.title,
          handle: node.handle,
          description: node.descriptionHtml || '<p>Sem descrição disponível.</p>',
          category: node.productType || 'Sem categoria',
          images,
          variants
        });

        setSelectedVariant(firstVariant);
        setSelectedImage(images[0] || firstVariant?.image?.url || '');
      } catch (error) {
        void error;
        toast.error('Erro ao carregar produto');
        setProduct(null);
      } finally {
        setLoading(false);
      }
    }

    if (id) fetchProduct();
  }, [id]);

  useEffect(() => {
    async function lookupJudgeMeProduct() {
      if (!product?.numericId) return;

      setJudgeMeProductId('');
      setReviews([]);
      setReviewsError('');

      try {
        const response = await fetch(
          `/api/judgeme-product?action=lookup&external_id=${encodeURIComponent(product.numericId)}&handle=${encodeURIComponent(product.handle)}`
        );

        const data = await response.json();

        if (!response.ok || !data?.ok) {
          setJudgeMeProductId('');
          setReviewsError(data?.error || 'Não foi possível localizar o produto no Judge.me');
          return;
        }

        const internalId = data?.product?.id ? String(data.product.id) : '';

        if (!internalId) {
          setJudgeMeProductId('');
          setReviewsError('Judge.me não retornou internal product id');
          return;
        }

        setJudgeMeProductId(internalId);
      } catch (error) {
        setJudgeMeProductId('');
        setReviewsError('Erro ao localizar produto no Judge.me');
      }
    }

    lookupJudgeMeProduct();
  }, [product?.numericId, product?.handle]);

  useEffect(() => {
    async function fetchJudgeMeReviews() {
      if (!judgeMeProductId) return;

      try {
        setReviewsLoading(true);
        setReviewsError('');
        setReviews([]);

        const firstResponse = await fetch(
          `/api/judgeme-product?action=reviews&internal_id=${encodeURIComponent(judgeMeProductId)}&page=1&per_page=100`
        );

        const firstData = await firstResponse.json();

        if (!firstResponse.ok || !firstData?.ok) {
          setReviews([]);
          setReviewsError(firstData?.error || 'Não foi possível carregar as avaliações');
          return;
        }

        const allReviews = Array.isArray(firstData?.reviews) ? [...firstData.reviews] : [];
        const PER_PAGE = 100;

        if (allReviews.length === PER_PAGE) {
          let page = 2;
          while (page <= 20) {
            const moreData = await fetch(
              `/api/judgeme-product?action=reviews&internal_id=${encodeURIComponent(judgeMeProductId)}&page=${page}&per_page=${PER_PAGE}`
            ).then((r) => r.json());
            if (!moreData?.ok || !Array.isArray(moreData?.reviews) || moreData.reviews.length === 0) break;
            allReviews.push(...moreData.reviews);
            if (moreData.reviews.length < PER_PAGE) break;
            page++;
          }
        }

        setReviews(allReviews);
        setVisibleReviewsCount(10);
      } catch (error) {
        void error;
        setReviews([]);
        setVisibleReviewsCount(10);
        setReviewsError('Erro ao carregar reviews do Judge.me');
      } finally {
        setReviewsLoading(false);
      }
    }

    fetchJudgeMeReviews();
  }, [judgeMeProductId]);

  useEffect(() => {
    if (!addToCartRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(addToCartRef.current);
    return () => observer.disconnect();
  }, [product]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => Math.min(i + 1, product.images.length - 1));
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, product]);

  useEffect(() => {
    if (!reviewLightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setReviewLightboxOpen(false);
      if (e.key === 'ArrowRight') setReviewLightboxIndex((i) => Math.min(i + 1, reviewPhotos.length - 1));
      if (e.key === 'ArrowLeft') setReviewLightboxIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [reviewLightboxOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviewsStats = useMemo(() => {
    const total = reviews.length;
    const average =
      total > 0
        ? reviews.reduce((acc, review) => acc + Number(review.rating || 0), 0) / total
        : 0;

    const withPhotos = reviews.filter((review) => review.has_pictures).length;
    const fiveStars = reviews.filter((review) => Number(review.rating) === 5).length;
    const verified = reviews.filter((review) => review.verified).length;

    return {
      total,
      average,
      withPhotos,
      fiveStars,
      verified,
      fiveStarsPercentage: total > 0 ? Math.round((fiveStars / total) * 100) : 0
    };
  }, [reviews]);

  const featuredReviews = useMemo(() => {
    return [...reviews]
      .sort((a, b) => {
        const aScore = (a.has_pictures ? 2 : 0) + (a.verified ? 1 : 0) + Number(a.rating || 0);
        const bScore = (b.has_pictures ? 2 : 0) + (b.verified ? 1 : 0) + Number(b.rating || 0);
        return bScore - aScore;
      })
      .slice(0, 3);
  }, [reviews]);

  const galleryImages = useMemo(() => {
    return reviews.flatMap((review) =>
      (review.pictures || []).map((picture, index) => ({
        url: picture,
        reviewId: review.id,
        reviewer: review.reviewer_name,
        index
      }))
    ).slice(0, 8);
  }, [reviews]);

  const reviewPhotos = useMemo(() =>
    reviews.flatMap((review) =>
      (review.pictures || []).map((url) => ({ url, reviewer: review.reviewer_name }))
    ),
    [reviews]
  );

  const visibleReviews = useMemo(() => {
  return reviews.slice(0, visibleReviewsCount);
}, [reviews, visibleReviewsCount]);

  const selectedPrice = selectedVariant ? parseFloat(selectedVariant.price.amount) : null;
  const selectedVariantUnavailable = !selectedVariant || !selectedVariant.availableForSale;

  function handleAddToCart() {
    if (!selectedVariant) {
      toast.error('Selecione uma opção');
      return;
    }

    if (!selectedVariant.availableForSale) {
      toast.error('Esta opção está esgotada');
      return;
    }

    addItem({
      ...product,
      variantId: selectedVariant.id,
      price: selectedPrice,
      image_url: selectedImage
    });

    toast.success('Produto adicionado ao carrinho!');
  }

  function handleToggleWishlist() {
    if (!product) return;
    const wasInWishlist = isInWishlist(product.id);
    toggleItem(product);
    toast.success(
      wasInWishlist
        ? 'Produto removido da wishlist'
        : 'Produto adicionado à wishlist'
    );
  }

  function formatReviewDate(dateString) {
    return formatDate(dateString) || 'Compra verificada';
  }

  function renderStars(rating, sizeClass = 'w-4 h-4') {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`${sizeClass} ${
          index < Math.round(Number(rating || 0))
            ? 'fill-accent text-accent'
            : 'text-gray-300'
        }`}
      />
    ));
  }
  function getDisplayName(name) {
  if (!name) return 'Cliente verificado';

  const normalized = name.toLowerCase().trim();

  if (normalized === 'anonymous' || normalized === 'anon') {
    return 'Cliente verificado';
  }

  return name;
}
  function getInitials(name) {
    if (!name) return 'C';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-12 animate-pulse">
            <div className="flex flex-col gap-4">
              <div className="w-full aspect-square bg-gray-100 rounded-2xl" />
              <div className="flex gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="w-16 h-16 bg-gray-100 rounded-md flex-shrink-0" />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="h-3 bg-gray-100 rounded w-1/4" />
              <div className="h-8 bg-gray-100 rounded w-3/4" />
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-10 bg-gray-100 rounded w-1/4" />
              <div className="h-20 bg-gray-100 rounded w-full" />
              <div className="flex gap-4 pt-4">
                <div className="h-12 bg-gray-100 rounded-full w-48" />
                <div className="h-12 bg-gray-100 rounded-full w-14" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-24 text-center">Produto não encontrado</div>;
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col"
          >
            <button
              type="button"
              className="w-full rounded-2xl mb-4 overflow-hidden cursor-zoom-in bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              onClick={() => {
                const idx = product.images.indexOf(selectedImage);
                setLightboxIndex(idx >= 0 ? idx : 0);
                setLightboxOpen(true);
              }}
              aria-label={`Ampliar imagem de ${product.name}`}
            >
              <img
                src={selectedImage}
                alt={product.name}
                className="w-full"
              />
            </button>

            <div className="flex gap-3 overflow-x-auto pb-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedImage(img)}
                  className={`w-16 h-16 border rounded-md flex-shrink-0 overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
                    selectedImage === img ? 'border-accent' : 'border-gray-200'
                  } hover:border-accent`}
                  aria-label={`Ver imagem ${i + 1} de ${product.name}`}
                >
                  <img
                    src={img}
                    alt={`${product.name} ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p className="text-sm text-text-muted mb-2">{product.category}</p>

            <h1 className="text-4xl font-bold mb-4">{product.name}</h1>

            <div className="flex items-center gap-2 mb-4">
              {reviewsStats.total > 0 ? (
                <>
                  <div className="flex items-center gap-1">
                    {renderStars(reviewsStats.average)}
                  </div>
                  <span className="font-medium">{reviewsStats.average.toFixed(1)}</span>
                  <span className="text-sm text-gray-500">
                    ({reviewsStats.total} avaliações)
                  </span>
                </>
              ) : (
                <>
                  <Star className="w-4 h-4 fill-accent text-accent" />
                  <span className="text-sm text-gray-500">Sem avaliações ainda</span>
                </>
              )}
            </div>

            <div className="flex items-baseline gap-3 mb-6">
              {selectedVariant?.compareAtPrice?.amount &&
                parseFloat(selectedVariant.compareAtPrice.amount) >
                  parseFloat(selectedVariant.price.amount) && (
                  <span className="text-lg text-text-muted line-through">
                    {parseFloat(selectedVariant.compareAtPrice.amount).toLocaleString(
                      'pt-BR',
                      { style: 'currency', currency: 'BRL' }
                    )}
                  </span>
                )}

              <span className="text-3xl font-bold text-accent">
                {selectedPrice !== null
                  ? selectedPrice.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })
                  : 'Indisponível'}
              </span>
            </div>

            {reviewsStats.total > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl border border-accent/20 bg-accent/5 p-4"
              >
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 border border-accent/10">
                    <BadgeCheck className="w-4 h-4 text-accent" />
                    <span>{reviewsStats.total} clientes avaliaram este produto</span>
                  </div>

                  {reviewsStats.withPhotos > 0 && (
                    <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 border border-accent/10">
                      <ImageIcon className="w-4 h-4 text-accent" />
                      <span>{reviewsStats.withPhotos} avaliações com foto</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 border border-accent/10">
                    <Star className="w-4 h-4 fill-accent text-accent" />
                    <span>{reviewsStats.fiveStarsPercentage}% deram 5 estrelas</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="mb-6">
              <p className="font-medium mb-2">Opções:</p>

              <div className="flex flex-wrap gap-2">
                {product.variants.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => {
                      setSelectedVariant(variant);
                      if (variant.image?.url) {
                        setSelectedImage(variant.image.url);
                      }
                    }}
                    disabled={!variant.availableForSale}
                    aria-pressed={selectedVariant?.id === variant.id}
                    className={`px-4 py-2 border rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 ${
                      selectedVariant?.id === variant.id
                        ? 'border-accent text-accent bg-accent/5'
                        : 'border-gray-300 hover:border-accent'
                    }`}
                  >
                    {variant.title}
                    {!variant.availableForSale && (
                      <span className="ml-2 text-xs text-text-muted">Esgotado</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div ref={addToCartRef} className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={selectedVariantUnavailable}
                className="bg-accent text-white px-8 py-6 rounded-full"
              >
                <ShoppingBag className="mr-2" />
                {selectedVariantUnavailable ? 'Indisponível' : 'Adicionar ao Carrinho'}
              </Button>

              <Button
                variant="outline"
                onClick={handleToggleWishlist}
                aria-label={isInWishlist(product?.id) ? 'Remover da wishlist' : 'Adicionar à wishlist'}
                className="rounded-full px-6"
              >
                <Heart />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 text-sm text-text-secondary">
              <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-accent/5 px-3 py-3">
                <Truck className="w-4 h-4 text-accent shrink-0" />
                <span>Frete grátis acima de R$ 150</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-accent/5 px-3 py-3">
                <ShieldCheck className="w-4 h-4 text-accent shrink-0" />
                <span>Pagamento seguro</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-accent/5 px-3 py-3">
                <RotateCcw className="w-4 h-4 text-accent shrink-0" />
                <span>Suporte pós-compra</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">
            Descrição do Produto
          </h2>

          <div
            className="text-gray-600 leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
          />
        </div>

        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6">
            Avaliações de Clientes
          </h2>

          {reviewsLoading ? (
            <p className="text-gray-500">Carregando avaliações...</p>
          ) : reviewsError ? (
            <p className="text-gray-500">Avaliações temporariamente indisponíveis.</p>
          ) : reviews.length > 0 ? (
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid lg:grid-cols-[320px_1fr] gap-6"
              >
                <div className="border rounded-2xl p-6 bg-white shadow-sm">
                  <p className="text-sm text-text-muted mb-2">Média das avaliações</p>

                  <div className="flex items-end gap-3 mb-3">
                    <span className="text-5xl font-bold text-accent">
                      {reviewsStats.average.toFixed(1)}
                    </span>
                    <span className="text-sm text-gray-500 pb-2">
                      de 5
                    </span>
                  </div>

                  <div className="flex items-center gap-1 mb-3">
                    {renderStars(reviewsStats.average, 'w-5 h-5')}
                  </div>

                  <p className="text-gray-600 text-sm">
                    Baseado em {reviewsStats.total} avaliações reais de clientes.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  {featuredReviews.map((review) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border rounded-2xl p-5 bg-white shadow-sm"
                    >
                      <Quote className="w-5 h-5 text-accent mb-3" />

                      <div className="flex items-center gap-1 mb-3">
                        {renderStars(review.rating)}
                      </div>

                      {review.title && (
                        <p className="font-semibold mb-2 text-[15px] leading-snug">
                          {review.title}
                        </p>
                      )}

                      <p className="text-sm text-gray-600 line-clamp-5">
                        {review.body}
                      </p>

                      <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                        {getDisplayName(review.reviewer_name)}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {galleryImages.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon className="w-5 h-5 text-accent" />
                    <h3 className="text-xl font-semibold">
                      Fotos reais de clientes
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {galleryImages.map((image) => (
                      <button
                        key={`${image.reviewId}-${image.index}`}
                        className="rounded-2xl overflow-hidden border bg-white shadow-sm cursor-zoom-in text-left"
                        onClick={() => {
                          const idx = reviewPhotos.findIndex((p) => p.url === image.url);
                          setReviewLightboxIndex(idx >= 0 ? idx : 0);
                          setReviewLightboxOpen(true);
                        }}
                      >
                        <img
                          src={image.url}
                          alt={`Avaliação de ${image.reviewer}`}
                          loading="lazy"
                          className="w-full h-48 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

                            <div className="space-y-4">
                {visibleReviews.map((review) => (
                  <motion.div
                    key={review.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border rounded-2xl p-5 bg-white shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-accent/10 text-accent flex items-center justify-center font-semibold shrink-0">
                        {getInitials(getDisplayName(review.reviewer_name))}
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <p className="font-semibold">{getDisplayName(review.reviewer_name)}</p>

                          <div className="flex items-center gap-1">
                            {renderStars(review.rating)}
                          </div>

                          <span className="text-sm text-gray-500">
                            {formatReviewDate(review.created_at)}
                          </span>

                          {review.verified && (
                            <span className="text-xs px-2 py-1 rounded-full bg-accent/10 text-accent">
                              Compra verificada
                            </span>
                          )}
                        </div>

                        {review.title && (
                          <p className="font-medium mb-2">{review.title}</p>
                        )}

                        <p className="text-gray-600 leading-relaxed">
                          {review.body}
                        </p>

                        {review.pictures?.length > 0 && (
                          <div className="flex gap-3 overflow-x-auto pt-4">
                            {review.pictures.map((picture, index) => (
                              <button
                                key={`${review.id}-picture-${index}`}
                                className="shrink-0 cursor-zoom-in"
                                onClick={() => {
                                  const idx = reviewPhotos.findIndex((p) => p.url === picture);
                                  setReviewLightboxIndex(idx >= 0 ? idx : 0);
                                  setReviewLightboxOpen(true);
                                }}
                              >
                                <img
                                  src={picture}
                                  alt={`Foto da avaliação ${index + 1}`}
                                  className="w-24 h-24 object-cover rounded-xl border"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {visibleReviewsCount < reviews.length && (
                  <div className="flex justify-center pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setVisibleReviewsCount((prev) => prev + 10)}
                      className="rounded-full px-6 border-accent text-accent hover:bg-accent hover:text-white transition-all duration-300"
                    >
                      Carregar mais avaliações
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">
              Nenhuma avaliação encontrada para este produto.
            </p>
          )}
        </div>
      </div>

      {lightboxOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            onClick={() => setLightboxOpen(false)}
            aria-label="Fechar"
          >
            <X className="w-8 h-8" />
          </button>

          {product.images.length > 1 && (
            <>
              <button
                className="absolute left-4 text-white/80 hover:text-white transition-colors disabled:opacity-20"
                disabled={lightboxIndex === 0}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i - 1); }}
                aria-label="Imagem anterior"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                className="absolute right-4 text-white/80 hover:text-white transition-colors disabled:opacity-20"
                disabled={lightboxIndex === product.images.length - 1}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => i + 1); }}
                aria-label="Próxima imagem"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}

          <img
            src={product.images[lightboxIndex]}
            alt={product.name}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />

          {product.images.length > 1 && (
            <div className="absolute bottom-4 flex gap-2">
              {product.images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                  className={`w-2 h-2 rounded-full transition-colors ${i === lightboxIndex ? 'bg-white' : 'bg-white/40'}`}
                  aria-label={`Ver imagem ${i + 1}`}
                />
              ))}
            </div>
          )}
        </motion.div>
      )}

      {reviewLightboxOpen && reviewPhotos.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setReviewLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            onClick={() => setReviewLightboxOpen(false)}
            aria-label="Fechar"
          >
            <X className="w-8 h-8" />
          </button>

          {reviewPhotos.length > 1 && (
            <>
              <button
                className="absolute left-4 text-white/80 hover:text-white transition-colors disabled:opacity-20"
                disabled={reviewLightboxIndex === 0}
                onClick={(e) => { e.stopPropagation(); setReviewLightboxIndex((i) => i - 1); }}
                aria-label="Foto anterior"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                className="absolute right-4 text-white/80 hover:text-white transition-colors disabled:opacity-20"
                disabled={reviewLightboxIndex === reviewPhotos.length - 1}
                onClick={(e) => { e.stopPropagation(); setReviewLightboxIndex((i) => i + 1); }}
                aria-label="Próxima foto"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}

          <img
            src={reviewPhotos[reviewLightboxIndex]?.url}
            alt={`Foto da avaliação de ${reviewPhotos[reviewLightboxIndex]?.reviewer}`}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />

          <p className="absolute bottom-6 text-white/60 text-sm">
            {reviewPhotos[reviewLightboxIndex]?.reviewer} · {reviewLightboxIndex + 1} / {reviewPhotos.length}
          </p>
        </motion.div>
      )}

      {showStickyBar && product && selectedVariant && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg px-6 py-4"
        >
          <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <img
                src={selectedImage}
                alt={product.name}
                className="w-12 h-12 rounded-lg object-cover shrink-0"
              />
              <div className="min-w-0">
                <p className="font-medium truncate text-sm">{product.name}</p>
                <p className="text-accent font-bold">
                  {selectedPrice.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                </p>
              </div>
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={selectedVariantUnavailable}
              className="bg-accent text-white px-8 py-3 rounded-full shrink-0"
            >
              <ShoppingBag className="mr-2 w-4 h-4" />
              Adicionar
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ProductPage;
